import classNames from "classnames";
import { compact, isEmpty, isEqual, uniqWith } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { connect } from "react-redux";
import { createSelector } from "reselect";
import {
  fetchCampaignTargetTypes,
  fetchOrderRegions,
  searchCurrencies,
  fetchCampaignsByThirdPartyCampaignId,
} from "../../../../../api";
import { BILLING_CYCLES, CAMPAIGN_STATUSES, CAMPAIGN_STEPS } from "../../../../../constants";
import styles from "../../../../../styles/CampaignCreate/CampaignForm.module.scss";
import { bindDispatch, showToast } from "../../../../../utils";
import Breadcrumb from "../../../../common/BreadCrumb";
import DuplicateCampaignWarning from "../../../../common/DuplicateCampaignWarning";
import InfoBlockLoading from "../../../../common/InfoBlockLoading";
import PageLoadingWithTabs from "../../../../common/PageLoadingWithTabs";
import CampaignTree from "../../CampaignStructure/CampaignTree";
import CampaignPageHeader from "../../common/CampaignPageHeader";
import { campaignStepChange, validateMandatoryCampaignFields } from "../../utils/campaignCreate";
import CampaignSteps from "../CampaignSteps";
import EmptyState from "../EmptyState";
import BasicDetails from "./BasicDetails";
import BillingDetails from "./BillingDetails";
import ContactDetails from "./ContactDetails";
import campaignRouteHandler from "..";

const orderIdType = ["Purchase Order ID", "DAVP Order ID"];

export class CampaignCreate extends Component {
  getInitialState = () => ({
    campaign: {
      name: "",
      advertiserId: "",
      countryIds: [],
      currencyId: "",
      campaignTargetTypeId: "",
      campaignTargetTypeName: "",
      billingInfo: {
        billingCycles: [],
        orderIdType: orderIdType[0],
        orderRegion: "",
      },
      // currency: {},
      maxPlaysPerScreen: 0,
      budgetedPlays: 0,
      effectiveRate: 0,
      campaignValue: null,
      validity: {},
      countries: [],
      contacts: [],
      thirdPartyCampaignId: "",
      orderId: "",
      status: CAMPAIGN_STATUSES.DRAFT,
    },
    orderIdType,
    campaignTypes: [],
    hasFormSaved: false,
    isFormEdited: false,
    isFetchingPreferences: false,
    isPageLoading: !this.props.isCampaignInvalid,
    duplicateThirdPartyCampaigns: [],
    showDuplicateCampaignWarning: false,
    disableSave: false,
  });

  state = this.getInitialState();

  static propTypes = {
    isCampaignInvalid: PropTypes.bool,
  };

  static defaultProps = {
    isCampaignInvalid: false,
  };

  formContainer = React.createRef();

  //! Old componentDidMount method that was replaced by the new one below (kept for reference in case of issues)
  //  componentDidMount = async () => {
  //    const {
  //      isCampaignInvalid,
  //      match: {
  //        params: { campaignId, action },
  //      },
  //      actions,
  //      history,
  //      campaignCreate: { campaign },
  //    } = this.props;
  //    if (isCampaignInvalid) return false;

  //    if (campaignId && isEmpty(action) && action !== "details") {
  //      history.replace(`/campaign-create/${campaignId}/details`);
  //    }
  //    await actions.getBillingCycles();
  //    await actions.getCountries({ ps: -1 });
  //    const campaignTypes = await fetchCampaignTargetTypes();
  //    if (campaignId) {
  //      await actions.updateActiveStep({
  //        campaignStep: CAMPAIGN_STEPS.CAMPAIGN,
  //        campaignId: campaignId,
  //      });
  //    }

  //    this.setState({
  //      campaign: {
  //        ...this.state.campaign,
  //        ...campaign,
  //        ...campaign.billingInfo,
  //      },
  //      campaignTypes: campaignTypes.data,
  //      isPageLoading: false,
  //    });
  //  };

  componentDidMount = () => {
    const fetchAndUpdate = async () => {
      const {
        isCampaignInvalid,
        match: {
          params: { campaignId, action },
        },
        actions,
        history,
        campaignCreate: { campaign },
      } = this.props;

      let updatedCampaign = { ...this.state.campaign, ...campaign };

      if (isCampaignInvalid) return false;

      if (campaignId && isEmpty(action) && action !== "details") {
        history.replace(`/campaign-create/${campaignId}/details`);
      }
      await actions.getBillingCycles();
      await actions.getCountries({ ps: -1 });
      const campaignTypes = await fetchCampaignTargetTypes();
      const orderRegions = await fetchOrderRegions();
      if (campaignId) {
        await actions.updateActiveStep({
          campaignStep: CAMPAIGN_STEPS.CAMPAIGN,
          campaignId: campaignId,
        });
      }
      const { data } = await searchCurrencies("INR");
      if (
        data &&
        data.length > 0 &&
        data[0].code === "INR" &&
        this.state.campaign.currencyId === ""
      ) {
        const [d] = data;
        updatedCampaign = {
          ...updatedCampaign,
          ...this.state.campaign,
          currencyId: d.id,
          currencyName: d.name,
        };
      }
      // Update the component's state with the new campaign data
      this.setState((prevState) => ({
        campaign: { ...prevState.campaign, ...updatedCampaign },
        campaignTypes: campaignTypes.data,
        orderRegions: orderRegions.data,
        isPageLoading: false,
      }));
    };
    fetchAndUpdate();
  };

  componentDidUpdate(prevProps) {
    const {
      campaignCreate: { campaign, activeStep },
      history,
      userData,
      countries,
    } = this.props;

    // Set default country only if needed and only if it changed
    if (
      (countries !== prevProps.countries || userData !== prevProps.userData) &&
      countries?.list?.data?.length &&
      userData?.company?.address?.country?.id
    ) {
      const userCountryId = userData.company.address.country.id;
      const hasDefaultCountry =
        this.state.campaign.countryIds && this.state.campaign.countryIds.includes(userCountryId);

      if (!hasDefaultCountry) {
        const updatedCampaign = this.setDefaultCountryIfNeeded(
          { ...this.state.campaign, ...campaign, ...campaign.billingInfo },
          countries,
          userData
        );
        this.setState({ campaign: updatedCampaign });
        return; // Avoid further setState in this update
      }
    }

    // Update campaign in state if campaign or step changed
    if (
      campaign?.id !== prevProps.campaignCreate.campaign?.id ||
      activeStep !== prevProps.campaignCreate.activeStep
    ) {
      this.setState({
        campaign: {
          ...this.state.campaign,
          ...campaign,
        },
      });
    }

    if (campaign?.id && prevProps.campaignCreate?.campaign?.id !== campaign.id) {
      if (validateMandatoryCampaignFields(campaign)) {
        history.push(`/campaign-create/${campaign.id}/target-groups`);
      } else {
        history.push(`/campaign-create/${campaign.id}/details`);
      }
      this.setState({
        campaign: {
          ...this.state.campaign,
          ...campaign,
        },
      });
    }
  }

  setDefaultCountryIfNeeded = (campaign, countries, userData) => {
    const countryList = countries?.list?.data;
    const userCountryId = userData.company?.address?.country?.id;

    if (
      Array.isArray(countryList) &&
      countryList.length > 0 &&
      userCountryId &&
      (!campaign.countryIds || !campaign.countryIds.includes(userCountryId))
    ) {
      const defaultCountry = countryList.find((c) => c.id === userCountryId);
      if (defaultCountry) {
        return {
          ...campaign,
          countryIds: [defaultCountry.id],
          countries: [{ id: defaultCountry.id, name: defaultCountry.name }],
        };
      }
    }
    return campaign;
  };

  // Sets state in campaign object on change of input
  onInputChange = (data, key) => {
    //SLATE-1546 Feb7 Anushree:- Utilizing functional setState and spread operator for non-mutative state updates.
    this.setState((prevState) => {
      let campaign = { ...prevState.campaign };
      if (key === "advertiserId") {
        campaign[key] = data.id;
        campaign.advertiserName = data.name;
      } else if (key === "countryIds") {
        if (!isEmpty(data)) {
          //SLATE-1428 Dec8 Anushree:- country filed is saved as countryIds, countries
          //campaign[key] = uniqWith([...campaign[key], data], isEqual);
          campaign[key] = uniqWith([...campaign[key], data.id], isEqual);
          campaign.countries = uniqWith(
            [...campaign.countries, { id: data.id, name: data.name }],
            isEqual
          );
        } else {
          campaign[key] = [];
          campaign.countries = [];
        }
      } else if (key === "currencyId") {
        campaign[key] = data.id;
        campaign.currencyName = data.name;
      } else if (key === "billingCycleId") {
        campaign[key] = data.id;
        campaign.billingCycle = data;
      } else if (key === "validity") {
        const [fromDate, toDate] = data;
        const validity = {
          fromDate: fromDate
            ? moment(fromDate).format("YYYY-MM-DD")
            : campaign.validity?.fromDate ?? null,
          toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : campaign.validity?.toDate ?? null,
        };
        campaign[key] = validity;
      } else if (key === "campaignTargetTypeId") {
        campaign["campaignTargetTypeName"] = data.name;
        campaign[key] = data.id;
      } else if (key.split(" ")[0] === "billingInfo") {
        if (key.split(" ")[1] === "billingCycleId") {
          campaign = {
            ...campaign,
            billingInfo: {
              ...campaign.billingInfo,
              billingCycleId: data?.id,
              billingCycles: data,
            },
          };
        } else {
          const keyParts = key.split(" ");
          campaign = {
            ...campaign,
            [keyParts[0]]: { ...campaign[keyParts[0]], [keyParts[1]]: data },
          };
        }
      } else {
        campaign[key] = data;
      }
      return {
        campaign,
        isFormEdited: true,
      };
    });
  };

  handleCountryRemove = (index) => {
    const { campaign } = this.state;
    const filteredIds = campaign.countryIds.filter((ids, idIndex) => idIndex !== index);
    const filteredCountries = campaign.countries.filter((country, idIndex) => idIndex !== index);
    this.setState({
      campaign: {
        ...campaign,
        countryIds: filteredIds,
        countries: filteredCountries,
      },
      isFormEdited: true,
    });
  };

  getBillingCycles = () => {
    const { campaign } = this.state;
    const { billingCycles } = this.props;
    if (billingCycles.isLoading) return [];
    const billingCycleList = billingCycles.data.data;
    const validBillingCycles =
      //SLATE-1396 Nov21 Anushree:- api returns field with code ON_CAMPAIGN_COMPLETION
      //billingCycleList.filter((bc) => bc.code === BILLING_CYCLES.CAMPAIGN_COMPLETION) ?? [];
      billingCycleList.filter((bc) => bc.code === BILLING_CYCLES.ON_CAMPAIGN_COMPLETION) ?? [];
    if (billingCycleList.length > 0 && campaign.validity?.fromDate && campaign.validity?.toDate) {
      const campaignDuration = moment(campaign.validity.toDate).diff(
        campaign.validity.fromDate,
        "days"
      );
      if (campaignDuration > 7)
        validBillingCycles.push(billingCycleList.find((bc) => bc.code === BILLING_CYCLES.WEEKLY));
      if (campaignDuration > 14)
        validBillingCycles.push(
          billingCycleList.find((bc) => bc.code === BILLING_CYCLES.FORTNIGHTLY)
        );
      if (campaignDuration > 30)
        validBillingCycles.push(billingCycleList.find((bc) => bc.code === BILLING_CYCLES.MONTHLY));
    }

    return compact(validBillingCycles);
  };

  handleSaveCampaignAsDraft = () => {
    const { campaign } = this.state;
    if (campaign.name) {
      this.props.actions.createOrUpdateCampaign(campaign);
    }

    this.setState({
      hasFormSaved: true,
      isFormEdited: false,
    });
  };

  handleCampaignDelete = async () => {
    const {
      history,
      match: { params },
    } = this.props;
    const resp = await this.props.actions.deleteCampaign(params.campaignId);
    if (resp && resp.success) {
      history.push(`/campaigns`);
    }
  };

  executeSaveAndContinue = async () => {
    const { isFormEdited } = this.state;
    //SLATE-1397 Nov21 Anushree:- need to wait for campaign creation to access the id returned in its response
    if (isFormEdited) await this.props.actions.createOrUpdateCampaign(this.state.campaign);
    const { id } = this.props.campaignCreate.campaign;
    // this.props.history.push(`/campaign-create/${campaign.id}/target-groups`);
    if (!!id) {
      this.props.history.push(`/campaign-create/${id}/target-groups`);
    }
    this.setState({
      hasFormSaved: true,
      isFormEdited: false,
      disableSave: false,
    });
  };

  handleChangeStep = async (preferredStep) => {
    const {
      match: { params },
      history,
      campaignCreate: { activeStep },
    } = this.props;
    const { campaign } = this.state;
    if (preferredStep === activeStep + 1 && validateMandatoryCampaignFields(campaign)) {
      this.setState({
        disableSave: true,
      });
      try {
        const { data } = await fetchCampaignsByThirdPartyCampaignId(campaign.thirdPartyCampaignId);
        if (data.filter((c) => c.id !== campaign.id).length > 0) {
          this.setState({
            showDuplicateCampaignWarning: true,
            duplicateThirdPartyCampaigns: data.filter((c) => c.id !== campaign.id),
          });
          return;
        }
        await this.executeSaveAndContinue();
      } catch (error) {
        showToast("Failed to check for duplicate campaigns. Please try again.", false);
      } finally {
        this.setState({ disableSave: false });
      }
    } else {
      campaignStepChange(history, params, activeStep, preferredStep);
      this.setState({
        hasFormSaved: true,
        isFormEdited: false,
      });
    }
  };

  handleFormReset = () => {
    const {
      match: {
        params: { campaignId },
      },
      campaignCreate: { campaign },
    } = this.props;
    if (campaignId) {
      this.setState({
        campaign: campaign,
        advertiser: campaign.advertiser,
        validity: campaign.validity,
        country: campaign.countries,
        hasFormSaved: false,
        isFormEdited: false,
      });
    } else {
      this.setState({
        ...this.getInitialState(),
        isPageLoading: false,
      });
    }
  };

  renderCampaignTree = () => {
    const { campaign } = this.state;
    const {
      match: {
        params: { campaignId },
      },
    } = this.props;

    //Summary not shown for new campaign step 1
    if (this.props.isCampaignInvalid || !campaign.id) return null;
    return (
      <div className={styles.campaignTree}>
        <CampaignTree
          //isNewCampaign
          // isLoading={isPageLoading}
          // campaignTreeData={campaign}
          // campaignCreate={campaignCreate}
          campaignIdForSummary={campaignId}
        />
      </div>
    );
  };

  renderFormContent = () => {
    const {
      isCampaignInvalid,
      userData,
      countries: { list: countries },
      actions: { getCompanyAdvertiserPreferences, getCountries },
      campaignCreate: { campaignTargetGroups },
    } = this.props;
    const { campaign, hasFormSaved, isPageLoading, campaignTypes, orderIdType } = this.state;
    const childProps = {
      campaign,
      campaignTypes,
      orderIdType,
      orderRegions: this.state.orderRegions,
      onInputChange: this.onInputChange,
      disabled: !campaign.advertiserId,
      hasFormSaved: hasFormSaved,
    };

    if (isPageLoading) return <InfoBlockLoading />;
    if (isCampaignInvalid) return <EmptyState type="Campaign" />;

    //SLATE-1399 Nov21 Anushree:- companyId, action:getCompanyAdvertiserPreferences passed in BasicDetails
    return (
      <div className={classNames("col-12 clearfix", styles.formContainer)}>
        <BasicDetails
          {...childProps}
          companyId={userData.company?.id}
          companyPreferences={userData.company?.preferences}
          countryList={countries?.data ?? []}
          //searchCountries={searchCountries}
          searchCountries={getCountries}
          removeCountryItem={this.handleCountryRemove}
          getCompanyAdvertiserPreferences={getCompanyAdvertiserPreferences}
          campaignTargetGroups={
            campaign.id && campaignTargetGroups.data ? campaignTargetGroups.data : []
          }
        />
        <ContactDetails {...childProps} />
        <BillingDetails {...childProps} billingCycles={this.getBillingCycles()} />
        <DuplicateCampaignWarning
          isOpen={this.state.showDuplicateCampaignWarning}
          onClose={() => this.setState({ showDuplicateCampaignWarning: false })}
          onIgnoreAndSave={() => {
            this.setState({ showDuplicateCampaignWarning: false, disableSave: true }, () => {
              this.executeSaveAndContinue();
            });
          }}
          thirdPartyCampaignId={campaign.thirdPartyCampaignId}
          campaigns={this.state.duplicateThirdPartyCampaigns}
        />
      </div>
    );
  };

  render() {
    const {
      history,
      campaignCreate: { isCampaignLoading, activeStep, campaignTargetGroups },
    } = this.props;
    const { campaign, hasFormSaved, isFormEdited, disableSave } = this.state;

    if (isCampaignLoading) return <PageLoadingWithTabs />;
    return (
      <div className="col-12 clearfix">
        <Breadcrumb
          history={history}
          links={[
            { name: "PageHeader.campaigns", path: "/campaigns" },
            {
              name: !campaign.id ? "PageHeader.newCampaign" : campaign.name,
              path: !campaign.id ? "/campaigns" : `/campaigns/campaigns/${campaign.id}/details`,
            },
            { name: "PageHeader.editCampaignDetails" },
          ]}
        />
        <div className={classNames("col-12 clearfix")}>
          <div className={styles.campaignCreateProgressBar}>
            <CampaignPageHeader
              title={!campaign.id ? "PageHeader.newCampaign" : campaign.name}
              onSaveCampaignAsDraft={this.handleSaveCampaignAsDraft}
              onDiscardCampaign={this.handleCampaignDelete}
              isDiscardDisabled={!campaign.id || campaign.status !== CAMPAIGN_STATUSES.DRAFT}
              hideDraftButton
            />
            <CampaignSteps
              history={history}
              activeStep={activeStep}
              validTillStep={campaignTargetGroups.data?.length > 0 ? activeStep + 1 : activeStep}
              hasFormSaved={hasFormSaved}
              disableReset={!isFormEdited}
              disableSave={disableSave}
              onChangeStep={this.handleChangeStep}
              onSaveDraft={this.handleSaveCampaignAsDraft}
              onReset={this.handleFormReset}
              isAdminUser={this.validateCreateCampaignAccess}
            />
          </div>
          <div className={classNames("col col-12", styles.pageContent)}>
            {this.renderFormContent()}
            {this.renderCampaignTree()}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = createSelector(
  (state) => state.campaignCreate,
  (state) => state.userData,
  (state) => state.billingCycles,
  (state) => state.countries,
  (campaignCreate, userData, billingCycles, countries) => ({
    campaignCreate,
    userData: userData.user,
    billingCycles,
    countries,
  })
);

export default connect(mapStateToProps, bindDispatch)(campaignRouteHandler(CampaignCreate));
